// Spec: .aidev/tasks/11083-preloader/docs/functional_requirements.md §Key decisions
//
// Mounts the bead field into the report's loading state. Owns everything that
// touches the page: which palette is active, whether motion is welcome, and the
// decision to hand back to the previous loader.

import { captureScrollAnchor, restoreScrollAnchor } from '@aytm/app/stats/lib/page/scroll-anchor'
import { isAdmin } from '@aytm/app/stats/lib/stat-page'

import PreloaderEngine from './engine'
import { mountSidebarShell, unmountSidebarShell } from '@aytm/app/stats/preloader/sidebar-shell'

// The report colour themes, in the order the Colors panel offers them.
const THEMES = [
  ['#8cc63f', '#60ac66', '#419982', '#1d83a2', '#1868a6', '#135182', '#003863'],
  ['#fcd921', '#fbb03b', '#f7931e', '#f15a24', '#ed1c24', '#c1272d', '#6e272d'],
  ['#c69c6d', '#a67c52', '#8c6239', '#754c24', '#603813', '#42210b', '#210f03'],
  ['#cccccc', '#b2b2b2', '#999999', '#808080', '#666666', '#4d4d4d', '#333333'],
  ['#cccccc', '#fbb03b', '#999999', '#8cc63f', '#666666', '#29abe2', '#333333'],
  ['#ed1c24', '#f7931e', '#fcd821', '#8cc63f', '#29abe2', '#0071bc', '#9e005d'],
  ['#ff0066', '#ff80b3', '#ffe6f0', '#e0e0e0', '#808080', '#404040', '#000000'],
  ['#f58000', '#ffc700', '#d75426', '#b73f7c', '#551b57', '#76c5e4', '#145a7b']
]

const HOST_ID = 'stats-preloader'

const hexRGB = h => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)]

// The report's model is the one authority on colour: it resolves the chosen theme and any custom
// palette, and it knows both before the charts exist and after they are rebuilt. The class on the
// chart container is written once at render, so it is absent during the load and stale after a
// theme change -- reading it is what made the field blue whatever the reader had picked.
const BANDS = 7

export function reportPalette() {
  const model = window.StatPage && window.StatPage.model
  const colours = model && 'function' === typeof model.colorTheme ? model.colorTheme() : null
  const usable = Array.isArray(colours) ? colours.filter(c => 'string' === typeof c && /^#[0-9a-f]{6}$/i.test(c)) : []
  return usable.length ? usable.slice(0, BANDS).map(hexRGB) : THEMES[0].map(hexRGB)
}

function prefersReducedMotion() {
  return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
}

function canRender() {
  if (typeof window.Path2D !== 'function') return false
  const probe = document.createElement('canvas')
  return Boolean(probe.getContext && probe.getContext('2d'))
}

// The loading and the failure screen are one block; the report's state picks its face.
const STATUS_REGION = '#report_status'

const MESSAGE_SELECTOR = '#report_status .stats-status__message'
const MESSAGE_PERIOD = 5000

// The report only populates this line once the server reports more than a
// minute of processing, so below that it stays blank. It is now the page's only
// "still working" signal, so the preloader keeps it fed from the first second.
const OWN_MESSAGES = [
  'Assembling your report, question by question.',
  'Weighing every answer against the rest of the sample.',
  'Taking soundings across every question.',
  'Reading the whole sample, so you can read the summary.',
  'Cross-tabulating. This is the patient part.',
  'Counting every respondent, checking every answer.'
]

let engine = null
let host = null
let messageTimer = null

// The report's own lines all explain the same ten-minute wait, so these say what
// the wait is for instead, and the two sets are interleaved rather than one
// replacing the other.
export function messagePool() {
  const own = window.page && window.page.lib && window.page.lib.loadingMessages
  const theirs = Array.isArray(own) ? own.map(stripMarkup).filter(t => t.trim().length > 1) : []
  if (!theirs.length) return OWN_MESSAGES.slice()

  // Interleaved, not appended: at five seconds a line, a set that waits its turn
  // at the back is a set nobody on a short report ever sees.
  const mixed = []
  for (let i = 0; i < Math.max(theirs.length, OWN_MESSAGES.length); i++) {
    if (i < OWN_MESSAGES.length) mixed.push(OWN_MESSAGES[i])
    if (i < theirs.length) mixed.push(theirs[i])
  }
  return mixed.filter((text, i) => mixed.indexOf(text) === i)
}

function stripMarkup(html) {
  const box = document.createElement('div')
  box.innerHTML = html
  return box.textContent || ''
}

// Rebuilt per character so each can reveal on its own delay.
function writeMessage(el, text) {
  el.textContent = ''
  el.classList.add('is-revealing')
  for (let i = 0; i < text.length; i++) {
    const span = document.createElement('span')
    span.textContent = text[i]
    span.style.animationDelay = `${(i * 0.016).toFixed(3)}s`
    el.appendChild(span)
  }
}

function startStatusLine() {
  const el = document.querySelector(MESSAGE_SELECTOR)
  if (!el || messageTimer) return
  const pool = messagePool()
  let i = 0
  writeMessage(el, pool[0])
  if (prefersReducedMotion()) return
  // Stepping in order, not at random: a random pick repeats often enough that
  // the reveal reads as nothing having happened.
  messageTimer = window.setInterval(() => {
    i = (i + 1) % pool.length
    writeMessage(el, pool[i])
  }, MESSAGE_PERIOD)
}

function stopStatusLine() {
  if (messageTimer) window.clearInterval(messageTimer)
  messageTimer = null
}

// The field simply stands down; it does not swap anything in. The previous dot
// loader stays parked in the markup as a manual revert, not as a runtime path --
// revealing it would start 52 infinite animations on the thread the report is
// loading on, which is the cost this replaced.
function standDown(reason) {
  if (host) host.setAttribute('hidden', 'hidden')
  if (engine) {
    engine.stop()
    engine = null
  }
  if (window.console && reason) window.console.info(`stats preloader: ${reason}`)
}

function applyErrorMode(on) {
  if (!engine) return
  engine.setError(on)
  if (on) {
    stopStatusLine()
    // the skeleton claims a report is still coming, which the failure screen has just denied
    unmountSidebarShell()
    return
  }
  startStatusLine()
}

export function mountPreloader(options = {}) {
  const { error = false } = options || {}
  host = document.querySelector(`#${HOST_ID}`)
  if (!host) return null

  // A running field switching between the two faces is a change of state, not a fresh start: the
  // beads it has already earned and the palette it has crossed to are both worth keeping.
  if (engine) {
    applyErrorMode(error)
    return engine
  }

  const canvas = host.querySelector('canvas')
  if (!canvas || !canRender()) {
    standDown('rendering surface unavailable')
    // the failure face has no line to feed
    if (!error) {
      startStatusLine()
      mountSidebarShell()
    }
    return null
  }

  try {
    engine = new PreloaderEngine(canvas, {
      palette: reportPalette(),
      seed: `aytm-${Math.floor(Math.random() * 1e6)}`,
      reducedMotion: prefersReducedMotion()
    })
  } catch {
    standDown('engine failed to start')
    if (!error) {
      startStatusLine()
      mountSidebarShell()
    }
    return null
  }

  host.removeAttribute('hidden')
  engine.start()
  if (error) {
    applyErrorMode(true)
    return engine
  }
  startStatusLine()
  mountSidebarShell()
  return engine
}

export function unmountPreloader() {
  if (pinned) return
  stopStatusLine()
  if (engine) {
    engine.stop()
    engine = null
  }
  if (host) host.setAttribute('hidden', 'hidden')
  unmountSidebarShell()
}

/** The reader picked a different theme or palette; cross the field over to it. */
export function preloaderRepalette() {
  if (engine) engine.setPalette(reportPalette())
}

// Ctrl+] pins the field: it keeps the content column instead of standing down, which is the only
// way to reach it once a report has landed. The report's own state machine keeps `hidden` on the
// region throughout, so unpinning hands the column back without touching that state.
const PIN_PATHS = [
  'M12 17v5',
  'M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z'
]

const ERROR_PATHS = [
  'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z',
  'M12 9v4',
  'M12 17h.01'
]

// One builder for both, so the pair cannot drift apart: same box, same mark, same label weight.
function buildTool(paths, label, name, title, onClick) {
  const button = document.createElement('button')
  button.type = 'button'
  button.className = `stats-preloader-tool ${name}`
  button.title = title
  const marks = paths.map(d => `<path d="${d}"></path>`).join('')
  button.innerHTML =
    `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" ` +
    `stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${marks}</svg><span>${label}</span>`
  button.addEventListener('click', onClick)
  return button
}

// What the held field stands in front of. The content blocks are the report's own state blocks, so
// the state machine rewrites their `hidden` on every state change and the pin has to be re-applied
// after it -- `#content-header-container` is no state block, so the pin owns its `hidden` outright
// and is the only thing that can hand it back.
const CONTENT_BLOCKS = ['#charts_content', '#specials_content', '#widget_content', '#data_quality_content']
const HEADER_BLOCKS = ['#content-header-container']

let pinned = false
let pinControl = null
let errControl = null
let toolRow = null
let heldAnchor = null
let heldVisibility = null
let erring = false

function buildPinControl() {
  return buildTool(PIN_PATHS, 'Pinned', 'stats-preloader-pin', 'Unpin to return to the report', () => setPinned(false))
}

// Debug only, and only reachable from the pinned state: lays the field down as the error state
// draws it, so the fall can be judged against the real palette and the real forms.
function buildErrorControl() {
  return buildTool(ERROR_PATHS, 'Error', 'stats-preloader-debug', 'Show the error state', () => setErring(!erring))
}

// The preview is a modifier on the status block: it shows the failure headline from the block's own
// markup, so the two cannot drift, and offers none of the failure's actions -- it is a debug view of
// the fall, not a failure.
function dressError(on) {
  const region = document.querySelector(STATUS_REGION)
  if (!region) return

  region.classList.toggle('stats-status--preview', on)
  if (on) stopStatusLine()
  else startStatusLine()
}

function setErring(next) {
  if (!engine || next === erring) return
  erring = Boolean(next)
  engine.setError(erring)
  dressError(erring)
  if (errControl) {
    errControl.classList.toggle('is-on', erring)
    errControl.title = erring ? 'Back to the loading field' : 'Show the error state'
  }
}

// Re-applied after the report's state machine has run, which rewrites `hidden` on every content
// block from the model and would otherwise hand the column straight back mid-pin.
export function applyPinnedVisibility() {
  if (!pinned) return

  for (const selector of HEADER_BLOCKS) document.querySelector(selector)?.classList.add('hidden')
  for (const selector of CONTENT_BLOCKS) document.querySelector(selector)?.classList.add('hidden')
  const region = document.querySelector(STATUS_REGION)
  region?.classList.remove('hidden')
  // the loading face, whatever state the report is in; the state's own modifier is left alone
  region?.classList.add('stats-status--pinned')
}

// The header is no state block, so the pin owns its `hidden` outright and is the only thing that can
// hand it back. The content blocks belong to the report's state machine, which alone knows which of
// the three special modes shares `#specials_content` -- so the truth is asked for rather than worked
// out again here, and the state it was in when the pin took hold is only the fallback for a release
// that happens before the view exists. Leaving the report hidden is the one outcome not allowed.
function releasePinnedVisibility() {
  document.querySelector(STATUS_REGION)?.classList.remove('stats-status--pinned')
  for (const selector of HEADER_BLOCKS) document.querySelector(selector)?.classList.remove('hidden')

  const view = window.StatPage?.view
  if (view?.updateState) view.updateState()
  else
    for (const held of heldVisibility ?? []) {
      document.querySelector(held.selector)?.classList.toggle('hidden', held.hidden)
    }

  heldVisibility = null
}

function setPinned(next) {
  const region = document.querySelector(STATUS_REGION)
  if (!region || next === pinned) return
  // the held field is a staff tool, so only an admin can raise it; letting go is never gated
  if (next && !isAdmin()) return

  if (next) {
    const settled = region.classList.contains('hidden')
    pinned = true
    if (!engine) mountPreloader()
    // Nothing to hold: on a surface that cannot draw the field, pinning would hide a finished report
    // behind an empty column -- the browsers this is supposed to degrade for are exactly those.
    if (!engine) {
      pinned = false
      return
    }
    // summoned over a finished report there is no material still arriving, so the field is handed
    // its full resolution rather than growing for half a minute
    if (settled) engine.addBeads(engine.o.beads)
    // Pinning hides the report, so a reader deep in a long one is left staring at the space it
    // used to fill. Note where they were, take them to the field, and give the place back when
    // they release it.
    heldAnchor = captureScrollAnchor()
    heldVisibility = [...HEADER_BLOCKS, ...CONTENT_BLOCKS, STATUS_REGION].map(selector => ({
      selector,
      hidden: Boolean(document.querySelector(selector)?.classList.contains('hidden'))
    }))
    applyPinnedVisibility()
    window.scrollTo(0, 0)
    if (!toolRow) {
      toolRow = document.createElement('div')
      toolRow.className = 'stats-preloader-tools'
      errControl = buildErrorControl()
      pinControl = buildPinControl()
      toolRow.append(errControl, pinControl)
    }
    region.appendChild(toolRow)
    return
  }

  pinned = false
  releasePinnedVisibility()
  if (!restoreScrollAnchor(heldAnchor)) window.scrollTo(0, 0)
  heldAnchor = null
  setErring(false)
  if (toolRow) toolRow.remove()
  if (region.classList.contains('hidden')) unmountPreloader()
}

function onShortcut(event) {
  if (pinned && 'Escape' === event.key) return setPinned(false)
  // matched on the physical key too: a non-Latin layout reports something else for the same one
  const bracket = 'BracketRight' === event.code || ']' === event.key
  if (!event.ctrlKey || event.altKey || event.metaKey || !bracket) return
  event.preventDefault()
  setPinned(!pinned)
}

/** Pin the field from the console when the keystroke is being eaten by something else. */
export function preloaderPin(next = true) {
  setPinned(Boolean(next))
}

export function installPreloaderPin() {
  if (window.__statsPreloaderPin) return
  window.__statsPreloaderPin = true
  window.addEventListener('keydown', onShortcut)
}

export default mountPreloader
